import {
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
  Overlay,
  OverlayContainer,
  OverlayRef,
} from '@angular/cdk/overlay';
import { OverlayConfig } from '@angular/cdk/overlay/overlay-config';
import { Platform } from '@angular/cdk/platform';
import { ComponentPortal } from '@angular/cdk/portal';
import { ViewportRuler } from '@angular/cdk/scrolling';
import { DOCUMENT } from '@angular/common';
import { ComponentRef, Directive, Inject, ViewContainerRef } from '@angular/core';

import { Subject } from 'rxjs';

import { OverlayPosition } from '@recruitee/prosemirror';

@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class WysiwygComponentPortalService<C> {
  protected componentRef: ComponentRef<C> | null;
  protected overlayRef: OverlayRef | null;
  protected positionStrategy: FlexibleConnectedPositionStrategy | null;
  protected ngOnDestroy$ = new Subject();
  protected overlayConfig: OverlayConfig = {};
  protected positions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
  ];

  private portalRef: ComponentPortal<C> | null;

  constructor(
    private overlay: Overlay,
    private vcRef: ViewContainerRef,
    private _viewportRuler: ViewportRuler,
    @Inject(DOCUMENT) private _document: Document,
    private _platform: Platform,
    private _overlayContainer: OverlayContainer,
  ) {}

  public ngOnDestroy(): void {
    this.close();
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public close(): void {
    this.componentRef?.destroy();
    this.componentRef = null;
    this.overlayRef?.dispose();
    this.overlayRef = null;
    this.positionStrategy?.dispose();
    this.positionStrategy = null;
  }

  public updatePosition(position: OverlayPosition): void {
    if (!this.overlayRef || !this.positionStrategy) return;

    this.positionStrategy.setOrigin({
      x: position.left,
      y: position.top,
      width: (position.right || position.left) - position.left,
      height: (position.bottom || position.top) - position.top,
    });
    this.overlayRef.updatePosition();
  }

  public isOpen(): boolean {
    return !!this.componentRef;
  }

  public openComponent(
    component: new (...args: any[]) => C,
    position: OverlayPosition,
    preferredPositons: ConnectedPosition[],
  ) {
    this.positionStrategy = new FlexibleConnectedPositionStrategy(
      {
        x: position.left,
        y: position.top,
        width: (position.right || position.left) - position.left,
        height: (position.bottom || position.top) - position.top,
      },
      this._viewportRuler,
      this._document,
      this._platform,
      this._overlayContainer,
    );
    this.positionStrategy.withGrowAfterOpen(true);
    this.positionStrategy.withPush(false);
    this.overlayRef = this.overlay.create({
      positionStrategy: this.positionStrategy,
      ...this.overlayConfig,
    });
    this.positionStrategy.withPositions([...preferredPositons, ...this.positions]);
    this.portalRef = new ComponentPortal(component, this.vcRef);
    this.componentRef = this.overlayRef.attach(this.portalRef);
  }
}
